import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Typography,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import AuthService from '../services/AuthService';

// Real bug fixed 2026-09-22: a real "forgot password" email with a real,
// working token led here -- but this page never existed. App.js had no
// /reset-password route at all, so clicking the email link fell through to
// the catch-all "/*" route and just showed the login screen, silently
// dropping the token. Modeled directly on EmailVerificationPage.js's
// checking -> pending -> confirming -> success/error state machine, with a
// password-entry form in place of email verification's one-click confirm.
const ResetPasswordPage = ({ colors }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('checking');
  const [message, setMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Real gap found 2026-09-29 (user-flagged): neither password field warned
  // about Caps Lock being on, which silently produces a password the user
  // did not intend to type -- especially painful here since the two fields
  // must match. Checked on every keystroke via the native
  // KeyboardEvent.getModifierState (works cross-browser, no library) --
  // shown as long as it's on for EITHER field, not just the one currently
  // focused, since a mismatch could stem from either one.
  const [capsLockOn, setCapsLockOn] = useState(false);
  const handlePasswordKeyEvent = (e) => {
    if (typeof e.getModifierState === 'function') {
      setCapsLockOn(e.getModifierState('CapsLock'));
    }
  };
  // Avoid a duplicate check call under React 18 StrictMode's dev-mode double-invoke.
  const checkAttempted = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      setMessage('No password reset token provided.');
      return;
    }

    if (checkAttempted.current) {
      return;
    }
    checkAttempted.current = true;

    const checkToken = async () => {
      try {
        const response = await AuthService.checkResetPasswordToken(token);
        if (response.valid) {
          setStatus('form');
        } else {
          setStatus('error');
          setMessage('This password reset link is invalid or has expired.');
        }
      } catch (error) {
        setStatus('error');
        setMessage(error.message || 'Failed to check reset link.');
      }
    };

    checkToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!newPassword) {
      setFormError('Please enter a new password');
      return;
    }
    if (newPassword.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    setStatus('confirming');
    try {
      const response = await AuthService.confirmResetPassword(token, newPassword);
      setStatus('success');
      setMessage(response?.message || 'Your password has been reset successfully!');
    } catch (error) {
      setStatus('form');
      setFormError(error.message || 'Failed to reset password. The link may be expired or invalid.');
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <>
            <CircularProgress size={64} sx={{ color: '#22c55e', mb: 2 }} />
            <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 2 }}>
              Checking Your Link
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              Please wait while we check your password reset link...
            </Typography>
          </>
        );

      case 'form':
        return (
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', textAlign: 'left' }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <LockIcon sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
              <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 1 }}>
                Reset Your Password
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                Enter a new password for your account.
              </Typography>
            </Box>

            {formError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>
                {formError}
              </Alert>
            )}

            {capsLockOn && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 0 }}>
                Caps Lock is on
              </Alert>
            )}

            <TextField
              fullWidth
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setFormError(''); }}
              onKeyUp={handlePasswordKeyEvent}
              onKeyDown={handlePasswordKeyEvent}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirm Password"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setFormError(''); }}
              onKeyUp={handlePasswordKeyEvent}
              onKeyDown={handlePasswordKeyEvent}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                bgcolor: '#22c55e',
                color: colors.background,
                py: 1.5,
                fontWeight: 600,
                letterSpacing: '0.1em',
                '&:hover': { bgcolor: '#16a34a' },
              }}
            >
              Reset Password
            </Button>
          </Box>
        );

      case 'confirming':
        return (
          <>
            <CircularProgress size={64} sx={{ color: '#22c55e', mb: 2 }} />
            <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 2 }}>
              Resetting Your Password
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              Please wait...
            </Typography>
          </>
        );

      case 'success':
        return (
          <>
            <CheckCircleIcon sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
            <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 2 }}>
              Password Reset!
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 3 }}>
              {message} You'll need to sign in again with your new password.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={handleGoToLogin}
              sx={{
                bgcolor: '#22c55e',
                color: colors.background,
                py: 1.5,
                fontWeight: 600,
                letterSpacing: '0.1em',
                '&:hover': { bgcolor: '#16a34a' },
              }}
            >
              Go to Login
            </Button>
          </>
        );

      case 'error':
        return (
          <>
            <ErrorIcon sx={{ fontSize: 64, color: '#ef4444', mb: 2 }} />
            <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 2 }}>
              Reset Link Failed
            </Typography>
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 0, bgcolor: 'rgba(239, 68, 68, 0.1)', borderLeft: '4px solid #ef4444' }}
            >
              {message}
            </Alert>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 3 }}>
              The link may have expired. You can request a new one from the login page.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={handleGoToLogin}
              sx={{
                bgcolor: '#22c55e',
                color: colors.background,
                py: 1.5,
                fontWeight: 600,
                letterSpacing: '0.1em',
                '&:hover': { bgcolor: '#16a34a' },
              }}
            >
              Go to Login
            </Button>
          </>
        );

      case 'no-token':
        return (
          <>
            <LockIcon sx={{ fontSize: 64, color: colors.textMuted, mb: 2 }} />
            <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 2 }}>
              Invalid Link
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 3 }}>
              {message}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={handleGoToLogin}
              sx={{
                bgcolor: '#22c55e',
                color: colors.background,
                py: 1.5,
                fontWeight: 600,
                letterSpacing: '0.1em',
                '&:hover': { bgcolor: '#16a34a' },
              }}
            >
              Go to Login
            </Button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: colors.background,
        p: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 400,
          bgcolor: colors.surface,
          border: `1px solid ${colors.border}`,
          p: 4,
          textAlign: 'center',
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ color: '#22c55e', fontWeight: 700, letterSpacing: '-0.025em', mb: 1 }}>
            Foysonis SCM
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            Password Reset
          </Typography>
        </Box>

        {renderContent()}
      </Box>
    </Box>
  );
};

export default ResetPasswordPage;
